/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStore } from "../anvilor-forms-app-store.service";
import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import { Observable } from "rxjs";
import { timer } from "rxjs";
import { switchMap } from "rxjs";
import { distinctUntilChanged } from "rxjs";
import { combineLatest } from "rxjs";
import { bsonUtil } from "@anvilor/ngx-apps-core";
import { map } from "rxjs/operators";

const OPEN_WHEN_INTERVAL = 500;
const OPEN_WHEN_GRACE_PERIOD = 10;

const CLOSE_WHEN_INTERVAL = 2000;
const CLOSE_WHEN_GRACE_PERIOD = 1000;

const notYetOpen = (now: Date, openWhen: Date | null) =>
  !!openWhen && now.getTime() < openWhen.getTime() - OPEN_WHEN_GRACE_PERIOD;

const isClosed = (now: Date, closeWhen: Date | null) =>
  !!closeWhen && now.getTime() > closeWhen.getTime() + CLOSE_WHEN_GRACE_PERIOD;

export class AnvilorFormsAppOpenClose {
  private store: AnvilorFormsAppStore;

  readonly openWhen$: Observable<Date | null>;
  readonly closeWhen$: Observable<Date | null>;
  readonly isBeforeSurveyOpen$: Observable<boolean>;
  readonly isAfterSurveyClose$: Observable<boolean>;
  readonly formIsClosed$: Observable<boolean>;
  readonly formIsOpen$: Observable<boolean>;

  constructor(private ctx: AnvilorFormsAppStoreContext) {
    this.store = ctx.store;

    this.openWhen$ = this.store
      .selectFromAnvilorFormConfig((c) => c.open_when)
      .pipe(map(bsonUtil.deserializeNode<Date | null>));

    this.closeWhen$ = this.store
      .selectFromAnvilorFormConfig((c) => c.close_when)
      .pipe(map(bsonUtil.deserializeNode<Date | null>));

    this.isBeforeSurveyOpen$ = this.openWhen$.pipe(
      switchMap((openWhen) =>
        this.clock$(OPEN_WHEN_INTERVAL).pipe(
          map((now) => notYetOpen(now, openWhen)),
          distinctUntilChanged()
        )
      )
    );

    this.isAfterSurveyClose$ = this.closeWhen$.pipe(
      switchMap((closeWhen) =>
        this.clock$(CLOSE_WHEN_INTERVAL).pipe(
          map((now) => isClosed(now, closeWhen)),
          distinctUntilChanged()
        )
      )
    );

    this.formIsClosed$ = combineLatest([
      this.isAfterSurveyClose$,
      this.isBeforeSurveyOpen$,
    ]).pipe(map(([afterClose, beforeOpen]) => afterClose || beforeOpen));

    this.formIsOpen$ = this.formIsClosed$.pipe(map((isClosed) => !isClosed));
  }

  private readonly clock$ = (_interval: number) =>
    timer(0, _interval).pipe(map(() => new Date()));
}
